import React, {
  useContext, useEffect, useState, useRef, useMemo,
} from 'react';
import StoryblokClient from 'storyblok-js-client';
import { Props } from '../../types';
import { Teaser, TeaserFields } from '../teaser';
import { PageStateContext, StoryblokService } from '../../../services';
import { getTeaserMapping, TeaserPageTypes } from '../../../services/compMapping/teasers';
import { getDynamicStories } from './get-dynamic-stories';
import { getPublicDynamicStories } from './get-public-dynamic-stories';

const DynamicTeaserContentTypes = {
  press: {
    component: 'page-press-news',
    requestSettings: {},
    filter: {
      authorized_roles: {},
    },
    searchParams: [],
  },
  story: {
    component: 'aktuelles',
    requestSettings: {},
    filter: {
      page_type: {
        in: 'story',
      },
    },
    searchParams: ['categories'],
  },
  blog: {
    component: 'aktuelles',
    requestSettings: {
      resolve_relations: 'aktuelles.categories',
    },
    filter: {
      page_type: {
        in: 'blog',
      },
    },
    searchParams: ['categories'],
  },
  did: {
    component: 'did-article-page',
    requestSettings: {
      resolve_relations: 'did-article-page.authors,did-article-page.categories',
    },
    searchParams: ['authors', 'categories'],
  },
  product: {
    component: 'page-product',
    requestSettings: {
      sort_by: 'first_published_at:desc',
    },
    searchParams: ['categories'],
  },
};

const teaserMappings = (article, blok, settings): TeaserFields | null => {
  if (!article) return null;
  return getTeaserMapping(blok.page_type)(article, {
    link_text: blok.link_text,
    size: blok.teaser_size,
    layout: blok.teaser_layout,
    invisible_date: blok.invisible_date,
  }, settings);
};

type PageTypeSettings = {
  component: string;
  requestSettings: object;
  filter?: object;
}

type DynamicTeaserFields = {
  page_type: TeaserPageTypes;
  rows_to_show: number;
  columns_layout: number;
  show_more_button: boolean;
  show_private_press: boolean;
  link_text?: string;
  teaser_size?: string;
}

type DynamicTeaserProps = Props<DynamicTeaserFields> & {
  filterSettings: object | null;
}

type BlokProps = {
  date?: string;
  description?: string;
  headline?: string;
  overline?: string;
  slotted_media?: {
    src?: {
      filename?: string;
    };
  }[];
  teaser_type?: string;
  hide_teaser_date?: boolean;
  teaser_url?: {
    url?: string;
  };
  full_slug?: string;
}

enum TeaserLayout {
  VARIANT_A = 'teaser-variant-a',
  VARIANT_B = 'teaser-variant-b',
  VARIANT_C = 'teaser-variant-c',
}

enum Appearance {
  DEFAULT = 'default',
  PRIMARY = 'primary',
  SECONDARY = 'secondary'
}

const applyAppearance = (item: TeaserFields | null, appearance: Appearance) => {
  if (!item || !appearance || appearance === Appearance.DEFAULT) return item;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const slotted_link = item.slotted_link?.map((link) => ({
    ...link,
    component: 'roche-button-global',
    icon: null,
    href: link.url.url,
    appearance,
  }));

  return { ...item, slotted_link };
};

const convertStoriesToTeasers = (items, blok, pageSettings) => items.map(
  (teaser) => applyAppearance(teaserMappings(teaser, blok, pageSettings), blok.link_appearance),
);

const prepareFilterSettings = (settings = {}) => Object.keys(settings).reduce(
  (accumulator, key) => ({
    ...accumulator,
    [key]: {
      all_in_array: settings[key],
    },
  }), {},
);

export const DynamicTeaser = (
  {
    blok, getComponent, slot, filterSettings = null,
  }: DynamicTeaserProps,
): JSX.Element => {
  const { setPictureCreditsAdditionalData, settings } = useContext(PageStateContext);

  const shouldShowPrivatePress = (
    blok.page_type === TeaserPageTypes.PRESS && blok.show_private_press
  );

  const storyblok = shouldShowPrivatePress
    ? getDynamicStories()
    : getPublicDynamicStories();

  const storyblokJsClient:
  React.MutableRefObject<null> | React.MutableRefObject<StoryblokClient> = useRef(null);

  // if we getting filter settings from outside then fetch data client side
  const getDataClientSide = useRef<boolean>(!!filterSettings);

  const moreButton = useRef<HTMLElement | null>(null);
  const isMoreButtonVisible = useRef<boolean>(false);

  const computedItemsToShow = Number(blok.rows_to_show) * (12 / Number(blok.columns_layout));

  const itemsToShow = useRef<number>(computedItemsToShow || 1);

  const totalStories = useRef<number>(0);
  const currentPage = useRef<number>(1);
  const filterParams = useRef(null);

  const [teasers, setTeasers] = useState([]);

  const fetchTeasers = async (): Promise<{ stories, total: number }> | null => {
    const { component, requestSettings, filter }:
    PageTypeSettings = DynamicTeaserContentTypes[blok.page_type];

    const { data, total } = await storyblokJsClient.current?.getStories({
      per_page: itemsToShow.current,
      page: currentPage.current,
      sort_by: 'content.release_date:desc',
      ...(requestSettings || {}),
      filter_query: {
        component: {
          like: component,
        },
        ...(filter || {}),
        ...(filterParams.current || {}),
        authorized_roles: shouldShowPrivatePress ? {} : {
          like: '[]',
        },
      },
    });

    return {
      stories: convertStoriesToTeasers(data.stories, blok, settings),
      total,
    };
  };

  const onMoreButtonClick = (): void => {
    fetchTeasers().then(({ stories, total }) => {
      totalStories.current = total;
      currentPage.current += 1;
      setTeasers((prev) => [...prev, ...stories]);
    });
  };

  const getHTMLComponent = (item: BlokProps, index: number, slotName?: string) => {
    const teaserMixTypes = [TeaserPageTypes.STORY, TeaserPageTypes.BLOG];

    if (teaserMixTypes.includes(blok.page_type)) {
      const sanitizedTeaserUrl = item?.teaser_url?.url?.startsWith('http') ? item?.teaser_url?.url : `/${item?.teaser_url?.url}`.replace('//', '/');
      return <roche-teaser-mix
        key={index}
        slot={slotName}
        layout={TeaserLayout.VARIANT_C}
        teaser-image={item?.slotted_media[0]?.src?.filename}
        teaser-title={item?.headline}
        teaser-overline={item?.overline}
        teaser-description={item?.description}
        teaser-date={item?.date}
        teaser-url={sanitizedTeaserUrl}
        story-type={item?.teaser_type}
        hide-teaser-date={item.hide_teaser_date}
      />;
    }

    return <Teaser blok={item} getComponent={getComponent} slot={slotName} key={index} />;
  };

  useEffect(() => {
    storyblokJsClient.current = new StoryblokClient({
      accessToken: StoryblokService.getConfig().options.accessToken as string,
    });
  }, []);

  useEffect(() => {
    getDataClientSide.current = filterSettings
      && Object.keys(filterSettings).some((item) => filterSettings[item] !== undefined);
    filterParams.current = getDataClientSide.current ? prepareFilterSettings(filterSettings) : null;

    if (getDataClientSide.current) {
      currentPage.current = 1;
      fetchTeasers().then(
        ({ stories, total }) => {
          totalStories.current = total;
          currentPage.current = 2;
          setTeasers(stories);
        },
        // eslint-disable-next-line no-console
        (error) => console.error(error.message),
      );
    } else {
      const initialStories = (storyblok[blok.page_type]?.items || []).slice(0, itemsToShow.current);
      totalStories.current = storyblok[blok.page_type]?.total;
      currentPage.current = initialStories.length >= itemsToShow.current ? 2 : 1;
      setTeasers(convertStoriesToTeasers(initialStories, blok, settings));
    }
  }, [filterSettings]);

  useEffect(() => {
    isMoreButtonVisible.current = teasers?.length < totalStories.current;
    setPictureCreditsAdditionalData([...teasers]);
  }, [teasers]);

  useEffect(() => {
    moreButton.current?.addEventListener('rocheButtonClick', onMoreButtonClick);
    return (): void => moreButton.current?.removeEventListener('rocheButtonClick', onMoreButtonClick);
  }, [moreButton.current]);

  if (itemsToShow.current === 1) {
    // To get the teaser layout according to teaser type uncomment next line
    // return teasers[0] ? getHTMLComponent(teasers[0], 0, slot) : null;
    return teasers[0] ? <Teaser blok={teasers[0]} getComponent={getComponent} slot={slot} /> : null;
  }

  const orderedTeasers = useMemo(() => [...teasers]
    .sort((a, b) => {
      if (!a.date || !b.date) return 0;
      return Date.parse(b.date) - Date.parse(a.date);
    }).map((item) => ({
      ...item,
      /* Setting the key for a random UUID to force react rerendering. */
      key: crypto.randomUUID(),
    })), [teasers]);

  return (
    <roche-teaser-list slot={slot}>
      {!orderedTeasers.length && <div slot="message">{settings?.did_no_results_message}</div>}
      <roche-columns center-children={'false'} multiple-rows={true}>
        {orderedTeasers.map((item, index) => (
          <roche-column key={item.key} span={blok.columns_layout}>
            {item && getHTMLComponent(item, index)}
          </roche-column>
        ))}
      </roche-columns>
      <div style={{
        display: blok.show_more_button && isMoreButtonVisible.current ? 'flex' : 'none',
        justifyContent: 'center',
      }}>
        <roche-button ref={moreButton} text={settings?.load_more_button_label} />
      </div>
    </roche-teaser-list>
  );
};
